import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import SockJS from "sockjs-client";
import Stomp from "stompjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch, faLock, faGear, faIdCard, faBell, faMapPin, faServer,
  faCircle, faCheck, faClock, faXmark, faSpinner, faPaperPlane,
  faTriangleExclamation, faBan, faTrashCan, faCode, faChevronDown,
  faChevronUp, faBolt, faTag,
} from "@fortawesome/free-solid-svg-icons";
import * as Constants from "../../utils/Constants";
import * as UrlConstants from "../../utils/UrlConstants";
import { getAuthToken, getClientIdOfUser, getLoggedInUser } from "../../utils/utility";
import {
  COMMANDS, COMMAND_CATEGORIES, SAFETY_LEVELS, ROLE_ACCESS,
  ALERT_SWITCH_OPTIONS, DEVICE_CLASSES,
} from "./commandCenterConstants";
import HomeSideNavBar from "../HomeSideNavBar";
import Header from "../Header";
import ApiDataProvider from "../ApiDataContext";
import BackToDashboardButton from "../../components/ui/BackToDashboardButton";
import "../../styles/components/command-center.css";

const BULK_BATCH_SIZE = 50;
const BULK_BATCH_DELAY_MS = 100;
const LOG_CAP = 2000;
const RESPONSE_CARDS_VISIBLE = 20;

// ── GL600 Protocol Response Parsers ────────────────────────────────
const WORKING_MODES = { 0: "SMS Mode", 1: "Power Saving", 2: "Real-time Tracking", 8: "Lock/Unlock Mode" };
const ALERT_SWITCH_LABELS = [
  "Lock Rope Tamper", "Unauthorized RFID", "Unlocking Alert", "Wrong Password",
  "Vibration", "Enter Geo-fence", "Exit Geo-fence", "Low Battery",
  "Back Cover Opened", "Motor Fault", "Jamming",
];
const ALERT_MODE_LABELS = { 0: "Disabled", 1: "GPRS Only", 2: "SMS Only", 3: "GPRS + SMS" };

const PROTOCOL_PARSERS = {
  P01: (parts) => ({
    title: "Firmware Version",
    fields: [{ label: "Firmware", value: parts[2] || "N/A" }],
  }),
  P02: (parts) => ({
    title: "Current Position",
    fields: [
      { label: "Latitude", value: parts[2] || "N/A" },
      { label: "Longitude", value: parts[3] || "N/A" },
      ...(parts[4] ? [{ label: "Altitude", value: parts[4] }] : []),
      ...(parts[5] ? [{ label: "Time", value: parts[5] }] : []),
    ],
  }),
  P14: (parts) => ({
    title: "IMEI Number",
    fields: [{ label: "IMEI", value: parts[2] || "N/A" }],
  }),
  P100: (parts) => ({
    title: "Real-time Status",
    fields: parts.slice(2).map((v, i) => ({ label: `Status Field ${i + 1}`, value: v })),
  }),
  P00: (parts) => ({
    title: "Working Mode",
    fields: [{ label: "Mode", value: WORKING_MODES[parseInt(parts[2])] || `Unknown (${parts[2]})` }],
  }),
  P04: (parts) => ({
    title: "Report Interval",
    fields: [
      { label: "Report Interval", value: `${parts[2] || "?"} seconds` },
      ...(parts[3] ? [{ label: "Wake-up Interval", value: `${parts[3]} minutes` }] : []),
    ],
  }),
  P39: (parts) => ({
    title: "Wake-up Duration",
    fields: [{ label: "Duration", value: `${parts[2] || "?"} minutes` }],
  }),
  P111: (parts) => ({
    title: "Lock/Unlock Report Interval",
    fields: [
      { label: "Lock Mode Interval", value: `${parts[2] || "?"} seconds` },
      ...(parts[3] ? [{ label: "Unlock Mode Interval", value: `${parts[3]} seconds` }] : []),
    ],
  }),
  P43: (parts) => {
    const status = parseInt(parts[2]);
    const wrongPwd = parseInt(parts[3]) || 0;
    return {
      title: "Unlock Response",
      fields: [
        { label: "Unlock Status", value: status === 1 ? "Success" : "Failed", status: status === 1 ? "success" : "error" },
        { label: "Wrong Password Attempts", value: String(wrongPwd), status: wrongPwd > 0 ? "warning" : "neutral" },
      ],
    };
  },
  P44: (parts) => {
    const result = parseInt(parts[2]);
    return {
      title: "Password Change",
      fields: [{ label: "Result", value: result === 1 ? "Success" : "Failed", status: result === 1 ? "success" : "error" }],
    };
  },
  P40: (parts) => {
    const switches = parts.slice(2);
    return {
      title: "Alert Switches",
      fields: switches.map((v, i) => ({
        label: ALERT_SWITCH_LABELS[i] || `Switch ${i + 1}`,
        value: ALERT_MODE_LABELS[parseInt(v)] || v,
      })),
    };
  },
  P36: (parts) => ({
    title: "Vibration Sensitivity",
    fields: [{ label: "Threshold", value: `${parts[2] || "?"} mg` }],
  }),
  P38: (parts) => ({
    title: "Unlock Alert Interval",
    fields: [{ label: "Interval", value: `${parts[2] || "?"} minutes` }],
  }),
  P41: (parts) => {
    // Query response: (deviceId,P41,groupIndex,count,key1,...keyN)
    // Action response: (deviceId,P41,result) where result=0 success
    if (parts.length <= 3) {
      const result = parseInt(parts[2]);
      return {
        title: "RFID Operation",
        fields: [{ label: "Result", value: result === 0 ? "Success" : `Error (code: ${parts[2]})`, status: result === 0 ? "success" : "error" }],
      };
    }
    const group = parts[2];
    const count = parts[3];
    const keys = parts.slice(4);
    return {
      title: "RFID Keys",
      fields: [
        { label: "Group", value: group },
        { label: "Key Count", value: count },
        ...(keys.length > 0 ? [{ label: "Keys", value: keys.join(", ") }] : [{ label: "Keys", value: "None registered" }]),
      ],
    };
  },
  P42: (parts) => ({
    title: "Batch RFID Enrollment",
    fields: [{ label: "Status", value: parseInt(parts[2]) === 1 ? "Started" : "Stopped" }],
  }),
  P15: () => ({
    title: "Reboot",
    fields: [{ label: "Status", value: "Device acknowledged reboot", status: "success" }],
  }),
  P32: () => ({
    title: "Force Sleep",
    fields: [{ label: "Status", value: "Device entering sleep mode", status: "success" }],
  }),
  P50: (parts) => ({
    title: "Power Switch",
    fields: [{ label: "Power Switch", value: parseInt(parts[2]) === 1 ? "Enabled" : "Disabled" }],
  }),
  P58: (parts) => ({
    title: "RFID-Geofence Link",
    fields: [{ label: "Restricted to Geo-fence", value: parseInt(parts[2]) === 1 ? "Yes" : "No" }],
  }),
  P112: (parts) => ({
    title: "Rope Cut Reminder",
    fields: [{ label: "Reminder", value: parseInt(parts[2]) === 1 ? "Enabled" : "Disabled" }],
  }),
};

/**
 * Parse a raw GL600 protocol response string into a structured object.
 * Raw format: (deviceId,ProtocolCode,param1,param2,...)
 */
function parseRawProtocolResponse(raw) {
  try {
    const trimmed = raw.trim();
    const inner = trimmed.startsWith("(") && trimmed.endsWith(")")
      ? trimmed.slice(1, -1)
      : trimmed;
    const parts = inner.split(",");
    if (parts.length < 2) return null;

    const deviceId = parts[0];
    const protocolCode = parts[1];
    const parser = PROTOCOL_PARSERS[protocolCode];

    if (parser) {
      const parsed = parser(parts);
      return { deviceId, protocolCode, ...parsed, rawResponse: raw };
    }

    // Fallback for unknown protocol codes
    return {
      deviceId,
      protocolCode,
      title: `Response ${protocolCode}`,
      fields: parts.slice(2).map((v, i) => ({ label: `Param ${i + 1}`, value: v })),
      rawResponse: raw,
    };
  } catch (e) {
    console.error("Failed to parse protocol response:", e);
    return null;
  }
}

const CATEGORY_ICONS = {
  QUERY: faSearch, LOCK_CONTROL: faLock, CONFIG: faGear,
  RFID: faIdCard, ALERTS: faBell, GEOFENCE: faMapPin, DEVICE_CONTROL: faServer,
  TRACKER_QUERY: faSearch, TRACKER_CONFIG: faGear, TRACKER_CONTROL: faBolt,
};

// True when a tracker asset speaks JT808 and therefore accepts remote commands.
// protocol is stamped by the backend on the device's first FT920 authentication;
// null/"GT06" trackers are listed but not selectable in the Trackers view.
const isFt920 = (device) => device?.protocol === "FT920";

// Maps frontend commandType enum names to their GL600 protocol codes.
// Used to correlate SENT log entries with raw device responses.
const COMMAND_TO_PROTOCOL = {
  QUERY_FIRMWARE: "P01", QUERY_POSITION: "P02", QUERY_IMEI: "P14", QUERY_STATUS: "P100",
  UNLOCK: "P43", CHANGE_PASSWORD: "P44",
  QUERY_WORKING_MODE: "P00", SET_WORKING_MODE: "P00",
  QUERY_REPORT_INTERVAL: "P04", SET_REPORT_INTERVAL: "P04",
  QUERY_WAKEUP_DURATION: "P39", SET_WAKEUP_DURATION: "P39",
  QUERY_LOCK_UNLOCK_INTERVAL: "P111", SET_LOCK_UNLOCK_INTERVAL: "P111",
  QUERY_RFID_KEYS: "P41", ADD_RFID_KEY: "P41", DELETE_RFID_KEY: "P41", DELETE_ALL_RFID_KEYS: "P41",
  QUERY_ALERT_SWITCHES: "P40", SET_ALERT_SWITCHES: "P40",
  QUERY_VIBRATION_SENSITIVITY: "P36", SET_VIBRATION_SENSITIVITY: "P36",
  QUERY_UNLOCK_ALERT_INTERVAL: "P38", SET_UNLOCK_ALERT_INTERVAL: "P38",
  QUERY_RFID_GEOFENCE_LINK: "P58", SET_RFID_GEOFENCE_LINK: "P58",
  REBOOT: "P15", FORCE_SLEEP: "P32",
  QUERY_POWER_SWITCH: "P50", SET_POWER_SWITCH: "P50",
  QUERY_ROPE_CUT_REMINDER: "P112", SET_ROPE_CUT_REMINDER: "P112",
};

const STATUS_ICONS = {
  SENT: { icon: faSpinner, className: "status-sent", spin: true },
  PENDING: { icon: faClock, className: "status-pending" },
  DELIVERED: { icon: faCheck, className: "status-delivered" },
  FAILED: { icon: faXmark, className: "status-failed" },
  EXPIRED: { icon: faClock, className: "status-expired" },
  CANCELLED: { icon: faBan, className: "status-cancelled" },
};

function CommandCenterContent() {
  const user = getLoggedInUser();
  const clientId = getClientIdOfUser();
  const userRole = localStorage.getItem("role") || "CLIENT_EMPLOYEE";

  // ── Device Selector State ─────────────────────────────
  // Which device class the Command Center is operating on: "LOCK" (GPS Locks,
  // GL600 protocol) or "TRACKER" (GPS Trackers — FT920/JT808 downlinks).
  const [deviceClass, setDeviceClass] = useState("LOCK");
  const [devices, setDevices] = useState([]);
  const [labels, setLabels] = useState([]);
  const [filterLabelIds, setFilterLabelIds] = useState([]);
  const [labelAssetIds, setLabelAssetIds] = useState(null); // null = no filter, Set = active filter
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);
  const [deviceSearch, setDeviceSearch] = useState("");
  const [labelSearchQuery, setLabelSearchQuery] = useState("");
  const [labelDropdownOpen, setLabelDropdownOpen] = useState(false);
  const labelInputRef = useRef(null);
  const labelDropdownRef = useRef(null);
  const [onlineDeviceIds, setOnlineDeviceIds] = useState(new Set());

  // ── Command Form State ────────────────────────────────
  const [activeCategory, setActiveCategory] = useState("QUERY");
  const [selectedCommand, setSelectedCommand] = useState("QUERY_FIRMWARE");
  const [formParams, setFormParams] = useState({});
  const [confirmText, setConfirmText] = useState("");
  const [isSending, setIsSending] = useState(false);

  // ── Bulk Send Progress ────────────────────────────────
  const [sendProgress, setSendProgress] = useState(null); // { total, sent, delivered, failed }
  const sendAbortRef = useRef(false);

  // ── Command Log State ─────────────────────────────────
  const [logEntries, setLogEntries] = useState([]);
  const [logTab, setLogTab] = useState("live"); // "live" or "pending"
  const [pendingCommands, setPendingCommands] = useState([]);

  // ── Device Response State (parsed protocol responses) ─
  const [deviceResponses, setDeviceResponses] = useState({});
  const [expandedRaw, setExpandedRaw] = useState({});
  const [showAllResponses, setShowAllResponses] = useState(false);

  const stompClientRef = useRef(null);
  const logEndRef = useRef(null);

  // ── Allowed categories for current role + device class ─
  const allowedCategories = ROLE_ACCESS[userRole] || ROLE_ACCESS.CLIENT_EMPLOYEE;
  const visibleCategories = COMMAND_CATEGORIES.filter(
    (c) => c.deviceClass === deviceClass && allowedCategories.includes(c.key)
  );

  // ── Fetch devices and labels on mount / class switch ──
  useEffect(() => {
    fetchDevicesAndLabels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceClass]);

  useEffect(() => {
    connectCommandResponseWs();
    return () => {
      if (stompClientRef.current?.connected) {
        stompClientRef.current.disconnect();
      }
    };
  }, []);

  const fetchDevicesAndLabels = async () => {
    try {
      axios.defaults.headers.common["Authorization"] =
        `${Constants.BEARER} ${getAuthToken()}`;

      const assetType =
        DEVICE_CLASSES.find((c) => c.key === deviceClass)?.assetType || "GPS_LOCK";
      const [devicesRes, labelsRes, onlineRes] = await Promise.all([
        axios.get(
          `${Constants.BASE_URL}${Constants.API_V1}/${clientId}/${Constants.EP_ASSETS_GET_ALL}`,
          { params: { assetType } }
        ),
        axios.get(`${Constants.BASE_URL}${Constants.API_V1}/${clientId}/asset-labels`),
        axios.get(`${Constants.BASE_URL}${Constants.API_V1}/${clientId}/${Constants.EP_ASSETS_ONLINE_STATUS}`),
      ]);

      // getAllAssets returns ApiResponse { responseData: [...] }
      const gpsLocks = devicesRes.data?.responseData || [];
      setDevices(gpsLocks);

      // Labels endpoint returns ApiResponse { responseData: [...] }
      setLabels(labelsRes.data?.responseData || []);

      // online-status returns ApiResponse { responseData: Set<String> (array of assetUnitIds) }
      const onlineSet = new Set(onlineRes.data?.responseData || []);
      setOnlineDeviceIds(onlineSet);
    } catch (err) {
      console.error("Error fetching devices/labels:", err);
    }
  };

  // ── Label filter: fetch asset IDs matching selected labels ─
  useEffect(() => {
    if (filterLabelIds.length === 0) {
      setLabelAssetIds(null);
      return;
    }
    const fetchLabelAssetIds = async () => {
      try {
        axios.defaults.headers.common["Authorization"] =
          `${Constants.BEARER} ${getAuthToken()}`;
        const res = await axios.get(
          `${Constants.BASE_URL}${Constants.API_V1}/${clientId}/asset-labels/assets`,
          { params: { labelIds: filterLabelIds.join(",") } }
        );
        const data = res.data?.responseData || [];
        const ids = new Set(data.map((d) => d.asset?.assetId).filter(Boolean));
        setLabelAssetIds(ids);
      } catch (err) {
        console.error("Error fetching label-filtered assets:", err);
        setLabelAssetIds(null);
      }
    };
    fetchLabelAssetIds();
  }, [filterLabelIds, clientId]);

  // ── Close label dropdown on outside click ─
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (labelDropdownRef.current && !labelDropdownRef.current.contains(e.target)) {
        setLabelDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ── WebSocket for command responses ───────────────────
  const connectCommandResponseWs = () => {
    const socket = new SockJS(Constants.BASE_URL + Constants.GPS_WEBSOCKET);
    const client = Stomp.over(socket);
    client.debug = null; // suppress STOMP debug logs

    client.connect({}, () => {
      // Subscribe to command responses for Command Center
      client.subscribe("/topic/command-responses", (msg) => {
        try {
          const body = msg.body;

          // Detect whether message is structured JSON or raw protocol
          const isRawProtocol = body.startsWith("(") && body.includes(",P");

          if (isRawProtocol) {
            // Raw device response: (deviceId,P01,V2.2.0)
            const parsed = parseRawProtocolResponse(body);
            if (parsed) {
              // Store as latest response for this device
              setDeviceResponses((prev) => ({
                ...prev,
                [parsed.deviceId]: { ...parsed, timestamp: new Date().toISOString() },
              }));
              // Track bulk progress: raw response = delivered
              setSendProgress((p) => p ? { ...p, delivered: p.delivered + 1 } : null);
              // Update the matching SENT entry for this device to DELIVERED,
              // rather than creating a duplicate entry with a stale spinner.
              // Match by deviceId + protocol code so concurrent commands to the
              // same device (e.g. Query Firmware + Query Status) resolve correctly.
              setLogEntries((prev) => {
                const updated = [...prev];
                const sentIdx = updated.findIndex(
                  (e) =>
                    e.deviceId === parsed.deviceId &&
                    e.status === "SENT" &&
                    COMMAND_TO_PROTOCOL[e.commandType] === parsed.protocolCode
                );
                // Fallback: match by deviceId only if no protocol-specific match
                const fallbackIdx =
                  sentIdx === -1
                    ? updated.findIndex(
                        (e) => e.deviceId === parsed.deviceId && e.status === "SENT"
                      )
                    : sentIdx;
                if (fallbackIdx !== -1) {
                  // Upgrade the SENT entry in-place
                  updated[fallbackIdx] = {
                    ...updated[fallbackIdx],
                    status: "DELIVERED",
                    message: parsed.title,
                    rawResponse: body,
                    parsedResponse: parsed,
                    timestamp: new Date().toISOString(),
                  };
                  return updated;
                }
                // No matching SENT entry — add as a new DELIVERED entry
                return [{
                  deviceId: parsed.deviceId,
                  commandType: parsed.protocolCode,
                  status: "DELIVERED",
                  message: parsed.title,
                  rawResponse: body,
                  parsedResponse: parsed,
                  timestamp: new Date().toISOString(),
                }, ...prev].slice(0, LOG_CAP);
              });
            }
          } else {
            // Structured JSON from DeviceCommandService (SENT, PENDING, FAILED, etc.)
            const data = JSON.parse(body);
            setLogEntries((prev) => {
              // Tracker (FT920) responses arrive as structured DELIVERED/FAILED with a
              // commandLogId — upgrade the matching SENT entry in place instead of adding
              // a duplicate row with a stale spinner (locks resolve via the raw path).
              if (
                (data.status === "DELIVERED" || data.status === "FAILED") &&
                data.commandLogId != null
              ) {
                const idx = prev.findIndex(
                  (e) => e.commandLogId === data.commandLogId && e.status === "SENT"
                );
                if (idx !== -1) {
                  const updated = [...prev];
                  updated[idx] = { ...updated[idx], ...data };
                  return updated;
                }
              }
              return [data, ...prev].slice(0, LOG_CAP);
            });
            // Track bulk progress counters
            if (data.status === "DELIVERED") {
              setSendProgress((p) => p ? { ...p, delivered: p.delivered + 1 } : null);
            } else if (data.status === "FAILED") {
              setSendProgress((p) => p ? { ...p, failed: p.failed + 1 } : null);
            }
            // Remove from pending if delivered/cancelled
            if (data.status === "DELIVERED" || data.status === "CANCELLED") {
              setPendingCommands((prev) =>
                prev.filter((p) => p.commandLogId !== data.commandLogId)
              );
            }
            if (data.status === "PENDING") {
              setPendingCommands((prev) => [data, ...prev]);
            }
          }
        } catch (e) {
          console.error("Error parsing command response:", e);
        }
      });

      // Also subscribe to device status for live online/offline updates
      client.subscribe(Constants.TOPIC_DEVICE_STATUS, (msg) => {
        try {
          const statusData = JSON.parse(msg.body);
          if (statusData?.deviceId && statusData?.status) {
            const isOnline = statusData.status === "ONLINE";
            setOnlineDeviceIds((prev) => {
              const next = new Set(prev);
              if (isOnline) {
                next.add(statusData.deviceId);
              } else {
                next.delete(statusData.deviceId);
              }
              return next;
            });
          }
        } catch (e) {
          console.error("Error parsing device status:", e);
        }
      });

      stompClientRef.current = client;
    });
  };

  // ── Send command to selected devices (batched for scale) ──
  const handleSendCommand = useCallback(async () => {
    if (!stompClientRef.current?.connected || selectedDeviceIds.length === 0) return;

    const cmdDef = COMMANDS[selectedCommand];
    if (!cmdDef) return;

    setIsSending(true);
    sendAbortRef.current = false;

    // Build params, handling special types
    const params = { ...formParams };
    if (cmdDef.fields.some((f) => f.key === "rfidKeys" && params.rfidKeys)) {
      params.rfidKeys = params.rfidKeys.split(",").map((k) => k.trim()).filter(Boolean);
    }

    const total = selectedDeviceIds.length;
    setSendProgress({ total, sent: 0, delivered: 0, failed: 0 });
    setDeviceResponses({});
    setExpandedRaw({});
    setShowAllResponses(false);

    // Batch send: send BULK_BATCH_SIZE at a time with a small delay between batches
    // to avoid overwhelming the WebSocket buffer and backend message queue.
    for (let i = 0; i < total; i += BULK_BATCH_SIZE) {
      if (sendAbortRef.current) break;
      const batch = selectedDeviceIds.slice(i, i + BULK_BATCH_SIZE);

      batch.forEach((deviceId) => {
        const payload = {
          deviceId,
          commandType: selectedCommand,
          params,
          actorId: user?.userId,
          actorUsername: user?.username || user?.sub,
          actorRole: userRole,
          clientId: parseInt(clientId),
        };
        stompClientRef.current.send(
          "/app/send-command",
          {},
          JSON.stringify(payload)
        );
      });

      setSendProgress((p) => p ? { ...p, sent: Math.min(i + batch.length, total) } : null);

      // Yield between batches to let the event loop breathe
      if (i + BULK_BATCH_SIZE < total) {
        await new Promise((r) => setTimeout(r, BULK_BATCH_DELAY_MS));
      }
    }

    setIsSending(false);
    setConfirmText("");
  }, [selectedCommand, formParams, selectedDeviceIds, user, userRole, clientId]);

  // ── Cancel a pending command ──────────────────────────
  const handleCancelCommand = (commandLogId) => {
    if (!stompClientRef.current?.connected) return;
    stompClientRef.current.send(
      "/app/cancel-command",
      {},
      JSON.stringify({
        commandType: "QUERY_FIRMWARE", // placeholder, not used for cancel
        params: { commandLogId },
        actorId: user?.userId,
        actorUsername: user?.username || user?.sub,
        actorRole: userRole,
        clientId: parseInt(clientId),
      })
    );
  };

  // ── Label autocomplete helpers ─────────────────────────
  const labelSuggestions = labels.filter((l) => {
    if (filterLabelIds.includes(l.labelId)) return false;
    if (!labelSearchQuery.trim()) return true;
    return l.labelName.toLowerCase().includes(labelSearchQuery.toLowerCase());
  });

  const selectedLabels = labels.filter((l) => filterLabelIds.includes(l.labelId));

  const addLabel = (labelId) => {
    setFilterLabelIds((prev) => [...prev, labelId]);
    setLabelSearchQuery("");
    setLabelDropdownOpen(false);
    labelInputRef.current?.focus();
  };

  const removeLabel = (labelId) => {
    setFilterLabelIds((prev) => prev.filter((id) => id !== labelId));
  };

  const clearFilters = () => {
    setFilterLabelIds([]);
    setDeviceSearch("");
    setLabelSearchQuery("");
    setLabelDropdownOpen(false);
  };

  const hasActiveFilters = filterLabelIds.length > 0 || deviceSearch;

  // ── Filter devices by labels and search ───────────────
  const filteredDevices = devices.filter((d) => {
    // Label filter: if labels are selected, only show matching devices
    if (labelAssetIds !== null && !labelAssetIds.has(d.assetId)) {
      return false;
    }
    const matchesSearch =
      !deviceSearch ||
      d.name?.toLowerCase().includes(deviceSearch.toLowerCase()) ||
      d.assetUnitId?.toLowerCase().includes(deviceSearch.toLowerCase());
    return matchesSearch;
  });

  const toggleDevice = (unitId) => {
    setSelectedDeviceIds((prev) =>
      prev.includes(unitId) ? prev.filter((id) => id !== unitId) : [...prev, unitId]
    );
  };

  const selectAllVisible = () => {
    const allIds = filteredDevices.filter(isSelectable).map((d) => d.assetUnitId);
    setSelectedDeviceIds(allIds);
  };

  const selectOnlineOnly = () => {
    const onlineIds = filteredDevices
      .filter((d) => isSelectable(d) && onlineDeviceIds.has(d.assetUnitId))
      .map((d) => d.assetUnitId);
    setSelectedDeviceIds(onlineIds);
  };

  // ── Command form helpers ──────────────────────────────
  const cmdDef = COMMANDS[selectedCommand];
  const safetyInfo = SAFETY_LEVELS[cmdDef?.safety] || SAFETY_LEVELS.SAFE;
  const isImmediate = cmdDef?.delivery === "IMMEDIATE_ONLY";
  const onlineSelected = selectedDeviceIds.filter((id) => onlineDeviceIds.has(id));
  const offlineSelected = selectedDeviceIds.filter((id) => !onlineDeviceIds.has(id));

  const canSend = () => {
    if (selectedDeviceIds.length === 0) return false;
    if (isImmediate && onlineSelected.length === 0) return false;
    if (cmdDef?.requireConfirmText && confirmText !== "CONFIRM") return false;
    // Check required fields
    for (const field of cmdDef?.fields || []) {
      if (field.required && !formParams[field.key]) return false;
    }
    return true;
  };

  const handleCategoryChange = (catKey) => {
    setActiveCategory(catKey);
    const firstCmd = Object.entries(COMMANDS).find(([, v]) => v.category === catKey);
    if (firstCmd) {
      setSelectedCommand(firstCmd[0]);
      setFormParams({});
      setConfirmText("");
    }
  };

  // Switching device class resets selection + jumps to that class's first category.
  const handleDeviceClassChange = (classKey) => {
    if (classKey === deviceClass) return;
    setDeviceClass(classKey);
    setSelectedDeviceIds([]);
    setDeviceSearch("");
    setFilterLabelIds([]);
    const firstCat = COMMAND_CATEGORIES.find(
      (c) => c.deviceClass === classKey && allowedCategories.includes(c.key)
    );
    if (firstCat) handleCategoryChange(firstCat.key);
  };

  // In the Trackers view only FT920 (JT808) devices accept commands.
  const isSelectable = (device) => deviceClass !== "TRACKER" || isFt920(device);

  const commandsInCategory = Object.entries(COMMANDS).filter(
    ([, v]) => v.category === activeCategory
  );

  // ── Render ────────────────────────────────────────────
  return (
    <div className="command-center">
      {/* Redesigned chrome strip — matches Dashboard / Tracking idiom. The
          original .command-center-header below is hidden via inline style
          so it doesn't double up but stays in the DOM as a fallback if
          something downstream depends on its class hooks. */}
      <header className="shrink-0 mx-2 mt-2 px-3 sm:px-4 py-2 bg-white border border-ink-200 rounded-xl shadow-xs flex items-center gap-3">
        <div className="h-8 w-8 rounded-lg bg-brand-50 grid place-items-center shrink-0">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-brand-700" aria-hidden="true"><polyline points="4 17 10 11 4 5"></polyline><line x1="12" y1="19" x2="20" y2="19"></line></svg>
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="text-sm sm:text-base font-semibold text-ink-900 leading-tight truncate">
            Command Center
          </h1>
          <p className="text-xs text-ink-500 truncate">
            Send commands to GPS Lock and GPS Tracker devices in real-time
          </p>
        </div>
        <BackToDashboardButton className="shrink-0" />
      </header>

      <div className="command-center-header" style={{display: "none"}}>
        <h2>Device Command Center</h2>
        <span className="cc-subtitle">
          Send commands to GPS Lock devices in real-time
        </span>
      </div>

      <div className="command-center-grid">
        {/* ── Left Panel: Device Selector ── */}
        <div className="cc-panel cc-device-selector">
          <div className="cc-panel-header">Device Selector</div>

          {/* Device class switch: GPS Locks ↔ GPS Trackers (FT920/JT808) */}
          <div className="cc-device-class-tabs" role="tablist">
            {DEVICE_CLASSES.map((cls) => (
              <button
                key={cls.key}
                role="tab"
                aria-selected={deviceClass === cls.key}
                className={`cc-device-class-tab ${deviceClass === cls.key ? "active" : ""}`}
                onClick={() => handleDeviceClassChange(cls.key)}
              >
                {cls.label}
              </button>
            ))}
          </div>

          <div className="cc-device-search">
            <input
              type="text"
              placeholder="Search devices..."
              value={deviceSearch}
              onChange={(e) => setDeviceSearch(e.target.value)}
            />
            {hasActiveFilters && (
              <button className="cc-clear-search-btn" onClick={clearFilters} title="Clear filters">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            )}
          </div>

          {/* Label autocomplete filter */}
          {labels.length > 0 && (
            <div className="cc-label-autocomplete" ref={labelDropdownRef}>
              <div className="label-input-wrapper">
                {selectedLabels.map((label) => (
                  <span key={label.labelId} className="label-tag">
                    {label.labelName}
                    <button
                      className="label-tag-remove"
                      onClick={() => removeLabel(label.labelId)}
                      title="Remove label"
                    >
                      <FontAwesomeIcon icon={faXmark} />
                    </button>
                  </span>
                ))}
                <div className="label-input-area">
                  <FontAwesomeIcon icon={faTag} className="label-input-icon" />
                  <input
                    ref={labelInputRef}
                    className="label-search-input"
                    type="text"
                    placeholder={selectedLabels.length > 0 ? "Add label..." : "Filter by label..."}
                    value={labelSearchQuery}
                    onChange={(e) => {
                      setLabelSearchQuery(e.target.value);
                      setLabelDropdownOpen(true);
                    }}
                    onFocus={() => setLabelDropdownOpen(true)}
                  />
                </div>
              </div>
              {labelDropdownOpen && labelSuggestions.length > 0 && (
                <ul className="label-suggestions">
                  {labelSuggestions.map((label) => (
                    <li
                      key={label.labelId}
                      className="label-suggestion-item"
                      onMouseDown={() => addLabel(label.labelId)}
                    >
                      <FontAwesomeIcon icon={faTag} className="label-suggestion-icon" />
                      {label.labelName}
                    </li>
                  ))}
                </ul>
              )}
              {labelDropdownOpen && labelSearchQuery && labelSuggestions.length === 0 && (
                <div className="label-suggestions">
                  <div className="label-no-match">No matching labels</div>
                </div>
              )}
            </div>
          )}

          {/* Clear all filters bar */}
          {hasActiveFilters && (
            <div className="cc-clear-filters-bar">
              <span className="clear-filters-summary">
                {filterLabelIds.length > 0 && (
                  <span className="filter-active-tag">
                    <FontAwesomeIcon icon={faTag} /> {filterLabelIds.length} label{filterLabelIds.length > 1 ? "s" : ""}
                  </span>
                )}
                {deviceSearch && (
                  <span className="filter-active-tag">
                    Search: &ldquo;{deviceSearch}&rdquo;
                  </span>
                )}
              </span>
              <button className="clear-all-filters-btn" onClick={clearFilters}>
                <FontAwesomeIcon icon={faXmark} />
                Clear All
              </button>
            </div>
          )}

          <div className="cc-device-actions">
            <button className="btn-sm" onClick={selectAllVisible}>Select All</button>
            <button className="btn-sm" onClick={selectOnlineOnly}>Online Only</button>
            <button className="btn-sm" onClick={() => setSelectedDeviceIds([])}>Clear</button>
          </div>

          <div className="cc-device-stats">
            <span>Selected: {selectedDeviceIds.length}</span>
            <span className="stat-online">
              Online: {filteredDevices.filter((d) => onlineDeviceIds.has(d.assetUnitId)).length}
            </span>
            <span className="stat-offline">
              Offline: {filteredDevices.filter((d) => !onlineDeviceIds.has(d.assetUnitId)).length}
            </span>
          </div>

          <div className="cc-device-list">
            {filteredDevices.map((device) => {
              const isOnline = onlineDeviceIds.has(device.assetUnitId);
              const isSelected = selectedDeviceIds.includes(device.assetUnitId);
              const selectable = isSelectable(device);
              return (
                <label
                  key={device.assetUnitId}
                  className={`cc-device-item ${isSelected ? "selected" : ""} ${selectable ? "" : "cc-device-unsupported"}`}
                  title={selectable ? undefined : "GT06 protocol — remote commands are supported for FT920 devices only"}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    disabled={!selectable}
                    onChange={() => toggleDevice(device.assetUnitId)}
                  />
                  <FontAwesomeIcon
                    icon={faCircle}
                    className={isOnline ? "dot-online" : "dot-offline"}
                  />
                  <span className="device-name">{device.name}</span>
                  {deviceClass === "TRACKER" && (
                    <span className={`cc-protocol-badge ${isFt920(device) ? "ft920" : "gt06"}`}>
                      {isFt920(device) ? "FT920" : "GT06"}
                    </span>
                  )}
                  <span className="device-id">{device.assetUnitId}</span>
                </label>
              );
            })}
            {filteredDevices.length === 0 && (
              <div className="cc-empty">
                {deviceClass === "TRACKER" ? "No GPS Tracker devices found" : "No GPS Lock devices found"}
              </div>
            )}
          </div>
        </div>

        {/* ── Center Panel: Command Form + Response Detail ── */}
        <div className="cc-center-column">
          {/* Command Form */}
          <div className="cc-panel cc-command-form">
            <div className="cc-panel-header">Command</div>

            {/* Category tabs */}
            <div className="cc-category-tabs">
              {visibleCategories.map((cat) => (
                <button
                  key={cat.key}
                  className={`cc-cat-tab ${activeCategory === cat.key ? "active" : ""} ${cat.key === "DEVICE_CONTROL" ? "danger-tab" : ""}`}
                  onClick={() => handleCategoryChange(cat.key)}
                >
                  <FontAwesomeIcon icon={CATEGORY_ICONS[cat.key]} />
                  <span>{cat.label}</span>
                </button>
              ))}
            </div>

            {/* Command selector within category */}
            <div className="cc-command-select">
              <select
                value={selectedCommand}
                onChange={(e) => {
                  setSelectedCommand(e.target.value);
                  setFormParams({});
                  setConfirmText("");
                }}
              >
                {commandsInCategory.map(([key, cmd]) => (
                  <option key={key} value={key}>{cmd.label}</option>
                ))}
              </select>
              <span
                className="safety-badge"
                style={{ backgroundColor: safetyInfo.color }}
              >
                {safetyInfo.label}
              </span>
            </div>

            {/* Dynamic form fields */}
            {cmdDef?.fields?.length > 0 && (
              <div className="cc-form-fields">
                {cmdDef.fields.map((field) => (
                  <div key={field.key} className="cc-field">
                    <label>{field.label}</label>
                    {field.type === "select" ? (
                      <select
                        value={formParams[field.key] ?? ""}
                        onChange={(e) =>
                          setFormParams((p) => ({ ...p, [field.key]: e.target.value }))
                        }
                      >
                        <option value="">-- Select --</option>
                        {field.options.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    ) : field.type === "alertSwitch" ? (
                      <select
                        value={formParams[field.key] ?? 1}
                        onChange={(e) =>
                          setFormParams((p) => ({ ...p, [field.key]: parseInt(e.target.value) }))
                        }
                      >
                        {ALERT_SWITCH_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    ) : field.type === "textarea" ? (
                      <textarea
                        value={formParams[field.key] || ""}
                        onChange={(e) =>
                          setFormParams((p) => ({ ...p, [field.key]: e.target.value }))
                        }
                        placeholder={field.label}
                        rows={3}
                      />
                    ) : (
                      <input
                        type={field.type}
                        value={formParams[field.key] || ""}
                        onChange={(e) =>
                          setFormParams((p) => ({ ...p, [field.key]: e.target.value }))
                        }
                        min={field.min}
                        max={field.max}
                        maxLength={field.maxLength}
                        placeholder={field.label}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Warning banner */}
            {cmdDef?.warning && (
              <div className={`cc-warning ${cmdDef.safety === "DANGEROUS" ? "danger" : ""}`}>
                <FontAwesomeIcon icon={faTriangleExclamation} />
                <span>{cmdDef.warning}</span>
              </div>
            )}

            {/* Offline device notice */}
            {offlineSelected.length > 0 && (
              <div className="cc-offline-notice">
                {isImmediate ? (
                  <span>
                    {offlineSelected.length} device(s) offline — command will only be sent to {onlineSelected.length} online device(s).
                  </span>
                ) : (
                  <span>
                    {offlineSelected.length} device(s) offline — command will be queued and delivered when they come online (expires in 60 min).
                  </span>
                )}
              </div>
            )}

            {/* Dangerous confirm text input */}
            {cmdDef?.requireConfirmText && (
              <div className="cc-confirm-input">
                <label>Type <strong>CONFIRM</strong> to proceed:</label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="CONFIRM"
                />
              </div>
            )}

            {/* Send button + progress */}
            <div className="cc-send-area">
              <button
                className={`cc-send-btn ${cmdDef?.safety === "DANGEROUS" ? "danger" : ""}`}
                disabled={!canSend() || isSending}
                onClick={handleSendCommand}
              >
                {isSending ? (
                  <FontAwesomeIcon icon={faSpinner} spin />
                ) : (
                  <FontAwesomeIcon icon={faPaperPlane} />
                )}
                <span>
                  {isImmediate
                    ? `Send to ${onlineSelected.length} device(s)`
                    : `Send to ${onlineSelected.length} online / Queue ${offlineSelected.length} offline`}
                </span>
              </button>

              {/* Bulk progress bar */}
              {sendProgress && sendProgress.total > 1 && (
                <div className="cc-bulk-progress">
                  <div className="bulk-progress-bar">
                    <div
                      className="bulk-progress-fill sent"
                      style={{ width: `${(sendProgress.sent / sendProgress.total) * 100}%` }}
                    />
                    <div
                      className="bulk-progress-fill delivered"
                      style={{ width: `${(sendProgress.delivered / sendProgress.total) * 100}%` }}
                    />
                  </div>
                  <div className="bulk-progress-stats">
                    <span>Dispatched: {sendProgress.sent}/{sendProgress.total}</span>
                    <span className="stat-delivered">
                      <FontAwesomeIcon icon={faCheck} /> {sendProgress.delivered}
                    </span>
                    {sendProgress.failed > 0 && (
                      <span className="stat-failed">
                        <FontAwesomeIcon icon={faXmark} /> {sendProgress.failed}
                      </span>
                    )}
                    {isSending && (
                      <button
                        className="btn-abort-send"
                        onClick={() => { sendAbortRef.current = true; }}
                      >
                        Abort
                      </button>
                    )}
                    {!isSending && (
                      <button
                        className="btn-dismiss-progress"
                        onClick={() => setSendProgress(null)}
                      >
                        Dismiss
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── Device Response Detail Panel ── */}
          <div className="cc-panel cc-response-detail">
            <div className="cc-panel-header">
              <span>Device Response</span>
              {Object.keys(deviceResponses).length > 0 && (
                <button
                  className="btn-clear-responses"
                  onClick={() => { setDeviceResponses({}); setShowAllResponses(false); }}
                >
                  Clear
                </button>
              )}
            </div>
            <div className="cc-response-content">
              {Object.keys(deviceResponses).length === 0 ? (
                <div className="cc-response-empty">
                  <FontAwesomeIcon icon={faCode} className="empty-icon" />
                  <span>Device responses will appear here when commands are executed</span>
                </div>
              ) : (
                <>
                  {/* Summary bar for bulk operations */}
                  {Object.keys(deviceResponses).length > RESPONSE_CARDS_VISIBLE && (
                    <div className="cc-response-summary">
                      <FontAwesomeIcon icon={faBolt} />
                      <span>
                        {Object.keys(deviceResponses).length} device response(s) received
                      </span>
                      <button
                        className="btn-toggle-all-responses"
                        onClick={() => setShowAllResponses((p) => !p)}
                      >
                        {showAllResponses ? "Show recent" : "Show all"}
                      </button>
                    </div>
                  )}
                  <div className="cc-response-cards">
                    {(showAllResponses
                      ? Object.entries(deviceResponses)
                      : Object.entries(deviceResponses).slice(-RESPONSE_CARDS_VISIBLE)
                    ).map(([devId, resp]) => {
                      const deviceName = devices.find((d) => d.assetUnitId === devId)?.name || devId;
                      const isExpanded = expandedRaw[devId];
                      return (
                        <div key={devId} className="cc-response-card">
                          <div className="response-card-header">
                            <div className="response-card-device">
                              <FontAwesomeIcon
                                icon={faCircle}
                                className={onlineDeviceIds.has(devId) ? "dot-online" : "dot-offline"}
                              />
                              <span className="response-device-name">{deviceName}</span>
                              <span className="response-device-id">{devId}</span>
                            </div>
                            <div className="response-card-meta">
                              <span className="response-protocol-badge">{resp.protocolCode}</span>
                              <span className="response-time">
                                {resp.timestamp ? new Date(resp.timestamp).toLocaleTimeString() : ""}
                              </span>
                            </div>
                          </div>
                          <div className="response-card-title">{resp.title}</div>
                          <div className="response-card-fields">
                            {resp.fields?.map((field, idx) => (
                              <div key={idx} className={`response-field ${field.status ? `field-${field.status}` : ""}`}>
                                <span className="response-field-label">{field.label}</span>
                                <span className="response-field-value">{field.value}</span>
                              </div>
                            ))}
                          </div>
                          <button
                            className="btn-toggle-raw"
                            onClick={() =>
                              setExpandedRaw((prev) => ({ ...prev, [devId]: !prev[devId] }))
                            }
                          >
                            <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                            <span>{isExpanded ? "Hide" : "Show"} Raw Response</span>
                          </button>
                          {isExpanded && (
                            <div className="response-raw-block">
                              <code>{resp.rawResponse}</code>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* ── Right Panel: Command Log ── */}
        <div className="cc-panel cc-command-log">
          <div className="cc-panel-header">
            Command Log
            <div className="cc-log-tabs">
              <button
                className={logTab === "live" ? "active" : ""}
                onClick={() => setLogTab("live")}
              >
                Live
              </button>
              <button
                className={logTab === "pending" ? "active" : ""}
                onClick={() => setLogTab("pending")}
              >
                Pending ({pendingCommands.length})
              </button>
            </div>
          </div>

          <div className="cc-log-list">
            {logTab === "live" ? (
              logEntries.length === 0 ? (
                <div className="cc-empty">No commands sent yet</div>
              ) : (
                logEntries.map((entry, idx) => {
                  const si = STATUS_ICONS[entry.status] || STATUS_ICONS.SENT;
                  return (
                    <div key={`${entry.commandLogId || entry.deviceId}-${idx}`} className={`cc-log-entry ${si.className}`}>
                      <div className="log-entry-header">
                        <FontAwesomeIcon icon={si.icon} spin={si.spin} />
                        <span className="log-device">{entry.deviceId}</span>
                        <span className="log-cmd">{entry.commandType}</span>
                        <span className="log-status">{entry.status}</span>
                      </div>
                      {entry.message && <div className="log-message">{entry.message}</div>}
                      {entry.parsedResponse?.fields && (
                        <div className="log-parsed-fields">
                          {entry.parsedResponse.fields.slice(0, 3).map((f, i) => (
                            <span key={i} className="log-parsed-field">
                              <span className="log-field-label">{f.label}:</span> {f.value}
                            </span>
                          ))}
                          {entry.parsedResponse.fields.length > 3 && (
                            <span className="log-parsed-more">+{entry.parsedResponse.fields.length - 3} more</span>
                          )}
                        </div>
                      )}
                      {!entry.parsedResponse && entry.rawResponse && (
                        <div className="log-response">{entry.rawResponse}</div>
                      )}
                      <div className="log-time">{entry.timestamp ? new Date(entry.timestamp).toLocaleTimeString() : ""}</div>
                    </div>
                  );
                })
              )
            ) : (
              pendingCommands.length === 0 ? (
                <div className="cc-empty">No pending commands</div>
              ) : (
                pendingCommands.map((entry) => (
                  <div key={entry.commandLogId} className="cc-log-entry status-pending">
                    <div className="log-entry-header">
                      <FontAwesomeIcon icon={faClock} />
                      <span className="log-device">{entry.deviceId}</span>
                      <span className="log-cmd">{entry.commandType}</span>
                      <button
                        className="btn-cancel-cmd"
                        onClick={() => handleCancelCommand(entry.commandLogId)}
                        title="Cancel queued command"
                      >
                        <FontAwesomeIcon icon={faTrashCan} />
                      </button>
                    </div>
                    <div className="log-message">{entry.message}</div>
                    <div className="log-time">
                      Queued: {entry.timestamp ? new Date(entry.timestamp).toLocaleTimeString() : ""}
                    </div>
                  </div>
                ))
              )
            )}
            <div ref={logEndRef} />
          </div>

          {logTab === "live" && logEntries.length > 0 && (
            <button className="btn-clear-log" onClick={() => setLogEntries([])}>
              Clear Log
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function DeviceCommandCenter() {
  return (
    <ApiDataProvider>
      <div className="container">
        <div className="screen-layout">
          <Header />
          <HomeSideNavBar />
          <CommandCenterContent />
        </div>
      </div>
    </ApiDataProvider>
  );
}
